/**
 * Chores' QPL client surface — thin wrapper around @yury-dymov/qpl-web.
 *
 * Initialization happens once at app boot via `initQPL()`. The browser
 * posts batches same-origin to `/api/v1/qpl/v1/ingest`; chores' Go
 * proxy validates the user's session and forwards to qpl-server with
 * the chores app key.
 */

import {
  init as initWeb,
  type Config as QPLConfig,
  QPLErrorReporter,
  LocalStorageErrorPersistence,
  DefaultQPLErrorNetwork,
} from "@yury-dymov/qpl-web";
import { installNavFlow } from "./nav-flow";

// Install the nav-flow at module evaluation time, before any saga
// middleware can run. As soon as the store module runs
// sagaMiddleware.run(rootSaga), the bootstrap sagas (auth, plugins,
// household, etc.) start firing — many of them call `withSagaFlow`,
// which needs an active nav flow to attach to. Without this side-effect
// install, those first sagas see a null active flow and own a root, so
// plugins.fetch / todos.fetch / calendar.fetch pollute the dashboard
// root list. The install is idempotent, so initQPL()'s explicit call
// below is harmless.
if (typeof window !== "undefined") {
  installNavFlow();
}

export {
  startFlow,
  emitPoint,
  finalizeFlow,
  setActiveFlow,
  pushActiveFlow,
  getActiveFlowId,
  getFlowRef,
  runInFlow,
  requestFlush,
} from "@yury-dymov/qpl-web";

export type {
  FlowHandle,
  FlowApiHandle,
  FlowRef,
  Outcome,
  StartFlowOptions,
  StoredFlow,
  StoredPoint,
} from "@yury-dymov/qpl-web";

const DEFAULT_CONFIG: QPLConfig = {
  endpoint: "/api/v1/qpl",
  appId: "chores",
  source: "saga",
  // The browser hits /api/v1/qpl/v1/ingest, which is wrapped in chores'
  // requireAuth — without a Bearer JWT it 401s and qpl-web drops the
  // batch. Pull the access token from the foauth session on every flush
  // so post-refresh tokens land cleanly; the session refreshes
  // proactively, so this usually returns synchronously from cache. An
  // empty string lets qpl-web's transport park the flush until the user
  // has signed in.
  getApiKey: async () => {
    try {
      // BFF auth: the browser holds only the HttpOnly chores_session
      // cookie, never a bearer. Return a sentinel so qpl-web's
      // transport gate (which refuses to flush when Authorization is
      // empty) is satisfied; chores' sessionMiddleware overwrites the
      // header with the real bearer it pulls from the cookie before
      // the request reaches requireAuth. Same-origin fetch attaches
      // the cookie automatically.
      return "bff-cookie";
    } catch {
      return "";
    }
  },
};

/**
 * Initialize QPL with chores defaults. Safe to call once per app boot.
 * Override fields by passing a partial config.
 */
export function initQPL(overrides: Partial<QPLConfig> = {}): void {
  initWeb({ ...DEFAULT_CONFIG, ...overrides });
  // Open a nav:<pathname> root flow now and re-open on every history
  // transition, so mount-effect fetches and sagas have a parent to
  // attach to instead of becoming sibling roots in the dashboard.
  installNavFlow();
}

// ---- QPLErrorReporter ----
//
// Module-level singleton. Independent of the flow-tracing init —
// reports cross-cutting client anomalies (display walker errors,
// sync rejects, parser-observation drops, etc.) per spec §3.5.
// Endpoint: /api/v1/qpl/v1/ingest/errors (chores proxies to
// qpl-server).

let _errorReporter: QPLErrorReporter | null = null;

/** Returns the singleton, constructing it on first access. */
export function qplErrorReporter(): QPLErrorReporter {
  if (_errorReporter) return _errorReporter;
  _errorReporter = new QPLErrorReporter({
    persistence: new LocalStorageErrorPersistence(),
    network: new DefaultQPLErrorNetwork(
      "/api/v1/qpl",
      async () => {
        try {
          // BFF auth: the browser holds only the HttpOnly chores_session
      // cookie, never a bearer. Return a sentinel so qpl-web's
      // transport gate (which refuses to flush when Authorization is
      // empty) is satisfied; chores' sessionMiddleware overwrites the
      // header with the real bearer it pulls from the cookie before
      // the request reaches requireAuth. Same-origin fetch attaches
      // the cookie automatically.
      return "bff-cookie";
        } catch {
          return "";
        }
      },
    ),
  });
  return _errorReporter;
}

export { installNavFlow } from "./nav-flow";
